import { useEffect, useState } from "react";

/** Public half of the worker's VAPID pair; the private half is in worker.env. */
const VAPID_PUBLIC_KEY =
  "BGeczswx2nng6fbvzOrIcdOEj0-EuOdYYPpJX3NEeyXawnDmMCXme2QeYGQPoEpDJ4qo1XYu2VMiQBflq-Ebtts";

function keyBytes(base64Url: string): Uint8Array<ArrayBuffer> {
  const base64 = base64Url.replaceAll("-", "+").replaceAll("_", "/");
  const raw = atob(base64 + "=".repeat((4 - (base64.length % 4)) % 4));
  return Uint8Array.from(raw, (char) => char.charCodeAt(0));
}

type State = "unsupported" | "install" | "off" | "on" | "blocked" | "busy";

/**
 * Phone alerts (Moss, 2 Nov 2026). iPhone only allows web push from the
 * home-screen app, so in Safari this says how to get there instead.
 */
export function PhoneAlerts({
  onSubscribe,
}: {
  onSubscribe: (subscription: PushSubscriptionJSON) => Promise<unknown>;
}) {
  const [state, setState] = useState<State>("busy");
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return setState("unsupported");
    if (!("PushManager" in window))
      return setState(
        /iPhone|iPad/.test(navigator.userAgent) ? "install" : "unsupported",
      );
    if (Notification.permission === "denied") return setState("blocked");
    void navigator.serviceWorker.ready
      .then((registration) => registration.pushManager.getSubscription())
      .then((existing) => setState(existing === null ? "off" : "on"))
      .catch(() => setState("off"));
  }, []);
  if (state === "unsupported" || state === "on" || state === "busy")
    return null;
  if (state === "install")
    return (
      <small className="phone-alerts-hint">
        Phone alerts: Share → Add to Home Screen, then open it from there.
      </small>
    );
  if (state === "blocked")
    return (
      <small className="phone-alerts-hint">
        Phone alerts are blocked in Settings → Notifications.
      </small>
    );
  return (
    <button
      type="button"
      className="button primary compact"
      title={error ?? undefined}
      onClick={() => {
        setState("busy");
        void (async () => {
          try {
            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.subscribe({
              userVisibleOnly: true,
              applicationServerKey: keyBytes(VAPID_PUBLIC_KEY),
            });
            await onSubscribe(subscription.toJSON());
            setState("on");
          } catch (failure) {
            setError(failure instanceof Error ? failure.message : "Failed");
            setState(Notification.permission === "denied" ? "blocked" : "off");
          }
        })();
      }}
    >
      {error === null ? "Turn on phone alerts" : "Retry phone alerts"}
    </button>
  );
}
